import { logger } from "@/utils/logger";
import { Module } from "vuex";
import { RootState, ValidateDocumentsState } from "@/types";
import {
  deleteManualDocument,
  deleteProcessFile,
  getGatheringProcessList, rejectFile, updateSupportingDocumentStatus
} from "@/store/services/task/actions/validateDocumentService";
import { deleteMetadataFile } from "@/commons/gathering-process";
import store from "@/store";

let gatheringProcessListPromise: Promise<void> | null = null;
let gatheringProcessListOfferUid: string | null = null;
let gatheringProcessListSeq = 0;

const validateDocumentsModule: Module<ValidateDocumentsState, RootState> = {
  namespaced: true,
  state: {
    gatheringProcessList: [],
    loadedOfferUid: null,
  },
  getters: {},
  actions: {
    async getGatheringProcessList({ commit, state }, payload?: { force?: boolean }) {
      const force = payload?.force === true;
      const offerUid = this.state.taskModule.offer?.resourceUid;
      if (!offerUid) {
        return;
      }

      const isCachedForOffer =
        !force &&
        state.loadedOfferUid === offerUid &&
        this.state.taskModule.gatheringProcess?.length > 0;

      if (isCachedForOffer) {
        const gatheringProcess = this.state.taskModule.gatheringProcess;
        commit("setProcessList", gatheringProcess);
        store.commit("middleOfficeModule/setGatheringProcessList", gatheringProcess);
        return;
      }

      if (gatheringProcessListPromise && gatheringProcessListOfferUid === offerUid && !force) {
        return gatheringProcessListPromise;
      }

      commit("setInitial");
      store.commit("taskModule/setGatheringProcess", []);

      gatheringProcessListOfferUid = offerUid;
      const loadSeq = ++gatheringProcessListSeq;
      const thisLoad: Promise<void> = (async () => {
        try {
          await getGatheringProcessList(offerUid);
          // Reponse tardive : une autre offre est ouverte, on n'ecrit rien.
          if (this.state.taskModule.offer?.resourceUid !== offerUid) return;
          const gatheringProcess = this.state.taskModule.gatheringProcess;
          commit("setProcessList", gatheringProcess);
          commit("setLoadedOfferUid", offerUid);
          store.commit("middleOfficeModule/setGatheringProcessList", gatheringProcess);
        } catch (e) {
          commit("setLoadedOfferUid", null);
          logger.error(e);
          throw e;
        } finally {
          // Ne liberer que notre propre chargement (pas celui d'une offre ouverte depuis).
          if (loadSeq === gatheringProcessListSeq) {
            gatheringProcessListPromise = null;
            gatheringProcessListOfferUid = null;
          }
        }
      })();
      gatheringProcessListPromise = thisLoad;
      return thisLoad;
    },
   async changeSupportingDocumentStatus({ commit }, payload) {
      const { status, gatheringProcessRow, processIndex } = payload
      if (!status || !gatheringProcessRow) {
        throw new Error('Supporting document and status are mandatory!')
      }
      return updateSupportingDocumentStatus(gatheringProcessRow,status).then(result => {
        return result;
        }).catch((e: Error) => logger.error(e))
    },
    deleteManualDocument({ commit }, payload) {
      const { request } = payload
      const processId = request.processResourceUid
      const supportingDocId = request.supportingDocumentResourceUid
      const documentResourceUid = request.receivedFileUid
      if (request && processId && supportingDocId) {
        rejectFile(processId, documentResourceUid, supportingDocId, {
          status: {
            objectType: "odm.supportingdocument.processstatus",
            systemUid: "odm-supportingdocument",
            resourceUid: "REJECTED"
          }
        }).then(() =>
          deleteManualDocument(processId, supportingDocId)
            .then(() => {
              if (payload.callback) payload.callback()
            })
            .catch((e) => logger.error(e))
        ).catch((e) => logger.error(e))
      }
    },
    async deleteProcessFile({ commit }, payload) {
      const { request, callback } = payload
      const processId = request.processResourceUid
      const documentResourceUid = request.receivedFileUid
      if (request && processId) {
        try {
          if (request.document?.metadata) {
            for (const metadata of request.document.metadata) {
              if (metadata.resourceUid) {
                await deleteMetadataFile(metadata.resourceUid);
              }
            }
          }
          callback('DECREMENT');
          await deleteProcessFile(processId, documentResourceUid);
        } catch (e) {
          logger.error(e);
        }
      }
    },
    cleanState({ commit }) {
      gatheringProcessListPromise = null;
      gatheringProcessListOfferUid = null;
      commit("setInitial");
    },
  },
  mutations: {
    setProcessList(state, payload) {
      state.gatheringProcessList = payload
    },
    setLoadedOfferUid(state, payload: string | null) {
      state.loadedOfferUid = payload;
    },
    updateSupportingDocument(state, payload) {
      const { supportingDocument, processIndex, indexSupportingDoc, indexRecivedFiles, status } = payload
      state.gatheringProcessList[processIndex ? processIndex : 0].supportingDocuments[indexSupportingDoc] = supportingDocument
      state.gatheringProcessList[processIndex ? processIndex : 0].gatheringProcessItem[0].receivedFiles[indexRecivedFiles].status.resourceUid = status
    },
    setInitial(state) {
      state.gatheringProcessList = []
      state.loadedOfferUid = null;
    },
  },
};

export default validateDocumentsModule;
